import { useEffect } from 'react';

/**
 * useResizableColumns
 *
 * Attaches drag-to-resize handles to every <th> in the given table ref.
 * Styled to match the REVA blush/taupe palette.
 *
 * @param {React.RefObject} tableRef      - ref attached to the <table> element
 * @param {boolean}         tableReady    - set to true once the table is rendered
 * @param {number[]}        initialWidths - optional array of starting column widths (px)
 */
export function useResizableColumns(tableRef, tableReady, initialWidths = []) {
  useEffect(() => {
    if (!tableReady) return;
    const table = tableRef.current;
    if (!table) return;

    const ths = Array.from(table.querySelectorAll('thead th'));
    const DEFAULT_WIDTH = 120;

    /* ── Set initial widths & force table-layout fixed ── */
    table.style.tableLayout = 'fixed';
    ths.forEach((th, i) => {
      th.style.width = (initialWidths[i] ?? DEFAULT_WIDTH) + 'px';
      th.style.minWidth = '50px';
      th.style.position = 'relative';
      th.style.userSelect = 'none';
    });

    /* ── State for active drag ── */
    let dragging = false;
    let activeTh = null;
    let startX = 0;
    let startW = 0;
    let activeLine = null;

    const onMouseMove = (e) => {
      if (!dragging || !activeTh) return;
      const diff = e.clientX - startX;
      const newW = Math.max(50, startW + diff);
      activeTh.style.width = newW + 'px';
    };

    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;

      /* restore body cursor & selection */
      document.body.style.cursor = '';
      document.body.style.userSelect = '';

      if (activeLine) {
        activeLine.style.background = 'rgba(185,167,146,0.3)';
        activeLine.style.height = '55%';
        activeLine = null;
      }
      activeTh = null;

      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    /* ── Attach resizer to each th except the last ── */
    ths.forEach((th, i) => {
      /* clean up any previous resizer */
      th.querySelector('.col-resizer')?.remove();

      if (i === ths.length - 1) return; /* no handle on last col */

      const resizer = document.createElement('div');
      resizer.className = 'col-resizer';
      Object.assign(resizer.style, {
        position: 'absolute',
        right: '0',
        top: '0',
        height: '100%',
        width: '10px',
        cursor: 'col-resize',
        userSelect: 'none',
        zIndex: '20',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      });

      const line = document.createElement('div');
      Object.assign(line.style, {
        width: '2px',
        height: '55%',
        background: 'rgba(185,167,146,0.3)',
        borderRadius: '2px',
        pointerEvents: 'none',
        transition: 'background 0.15s, height 0.15s',
      });
      resizer.appendChild(line);

      /* hover effects */
      resizer.addEventListener('mouseenter', () => {
        if (!dragging) {
          line.style.background = '#9e8872';
          line.style.height = '70%';
        }
      });
      resizer.addEventListener('mouseleave', () => {
        if (!dragging) {
          line.style.background = 'rgba(185,167,146,0.3)';
          line.style.height = '55%';
        }
      });

      /* drag start */
      resizer.addEventListener('mousedown', (e) => {
        e.preventDefault();   /* prevent text selection */
        e.stopPropagation();

        dragging = true;
        activeTh = th;
        activeLine = line;
        startX = e.clientX;
        startW = th.getBoundingClientRect().width; /* use actual rendered width */

        line.style.background = '#9e8872';
        line.style.height = '80%';

        /* lock cursor globally while dragging */
        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
      });

      th.appendChild(resizer);
    });

    /* ── Cleanup ── */
    return () => {
      ths.forEach(th => th.querySelector('.col-resizer')?.remove());
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [tableReady]);
}